import mergeWith from 'lodash/mergeWith';
import omit from 'lodash/omit';
import isObject from 'lodash/isObject';
import uniq from 'lodash/uniq';
import set from 'lodash/set';
import { ThemeMode } from '@/interfaces/widgetTheme';
import deepClone from '@/utils/deepClone';
import { PREVIEW_BASE_URL, SYSTEM_THEME_ID } from '@/config/widgetTheme';
import { grayColorPallet } from '@/config/colorPallets/universalDesignSystem';
import { toRGB } from '@/utils/colors';
import { arrayOverwriteStrategy } from '@/utils/objects';
import { kebabCase } from '@/utils/strings';
import { cleanPalettes } from './utils';
import { createTheme } from './createTheme';
const PREFIX = 'common: services: widgetTheme:'; // Merges theme with its variants based on mode

export const mergeThemeWithVariants = (theme, themeMode) => {
  var _merged$variants;

  let merged = deepClone(theme); // Then merge the specific mode variant if it's not light

  if ((_merged$variants = merged.variants) !== null && _merged$variants !== void 0 && _merged$variants[themeMode] && themeMode !== ThemeMode.Light) {
    merged = mergeWith({}, merged, merged.variants[themeMode], arrayOverwriteStrategy);
  } // Cleans color palettes by removing invalid entries
  // and ensuring consistent data structure


  if (merged.colorPallets.length > 0) {
    const cleanedPalettes = cleanPalettes(merged.colorPallets);
    set(merged, 'colorPallets', cleanedPalettes);
  } // Remove variants from the final theme


  return omit(merged, 'variants');
}; // Merges multiple theme configurations in sequence

export const mergeThemes = function (baseTheme) {
  let merged = createTheme({
    overrides: baseTheme
  });

  for (var _len = arguments.length, themeOverrides = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    themeOverrides[_key - 1] = arguments[_key];
  }

  for (const overrides of themeOverrides) {
    if (!overrides) continue;
    merged = mergeWith(merged, createTheme({
      overrides
    }), arrayOverwriteStrategy);
  }

  return merged;
}; // Parse a padding multiplier (e.g. "1_5") to get its numeric value (1.5)

export const getPaddingMultiplierFromKey = key => {
  const value = parseFloat(key.replace('_', '.'));
  return isNaN(value) ? 0 : value;
}; // Returns the radius multiplier based on the key

export const getRadiusMultiplier = key => {
  const multipliers = {
    rounded: 1,
    'rounded-sm': 0.5,
    'rounded-md': 1.5,
    'rounded-lg': 2,
    'rounded-xl': 3,
    'rounded-2xl': 4
  };
  return multipliers[key] || 0;
};
export const getBaseGroupStyles = group => {
  return Object.keys(group).reduce((acc, key) => {
    if (!isObject(group[key])) {
      acc[key] = group[key];
    }

    return acc;
  }, {});
};
// Prepares a theme setup to update the selected theme values.
// Identifies only the style properties that are different from the default theme.
export function changeTheme(theme) {
  var _colorPalletes$;

  let themeMode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ThemeMode.Light;

  if (!theme.id) {
    throw Error(`${PREFIX} theme.id is required`);
  } // Switch to the first color palette on theme change


  const colorPalletes = getModeColorPalettes(theme, themeMode);
  const colorPallet = ((_colorPalletes$ = colorPalletes[0]) === null || _colorPalletes$ === void 0 ? void 0 : _colorPalletes$.colors) || [];
  return {
    theme: theme.id,
    themeMode: themeMode,
    colorPallet: colorPallet.filter(Boolean),
    shared: Boolean(theme.shared),
    slug: theme.slug || ''
  };
} // Create a list of theme swatches based on the current theme
// and active color palette, including (in order):
// palette colors, any black, any white, body font color, header font color,
// and all the theme's gray colors.

export const getDefaultColorPresets = (config, baseColorPresets) => {
  const themeColorPallet = config.colorPallet || [];
  return uniq([...themeColorPallet, ...baseColorPresets].filter(Boolean) // Remove any null values
  .map(toRGB) // Convert to RGB for consistency and to remove duplicates
  );
};
export const getBaseColorPresets = theme => {
  return uniq([theme.color.gray100, theme.color.white, theme.color.bodyFont, theme.color.headingFont, ...grayColorPallet].filter(Boolean) // Remove any null values
  );
}; // Returns the color palettes for a given theme and mode.

export const getModeColorPalettes = (theme, themeMode) => {
  var _theme$variants, _theme$variants$theme;

  // If no color palettes found for the mode,
  // fallback to the default light theme palettes
  let colorPallets = theme.colorPallets || []; // Get cleaned color palettes based on the theme mode

  const modeColorPallets = (_theme$variants = theme.variants) === null || _theme$variants === void 0 ? void 0 : (_theme$variants$theme = _theme$variants[themeMode]) === null || _theme$variants$theme === void 0 ? void 0 : _theme$variants$theme.colorPallets;

  if (themeMode !== ThemeMode.Light && modeColorPallets !== null && modeColorPallets !== void 0 && modeColorPallets.length) {
    colorPallets = modeColorPallets;
  } // Clean the color palettes to ensure they are valid


  return cleanPalettes(colorPallets);
}; // Creates all system theme variants from base configurations

export const createSystemThemeVariants = themeOptions => {
  const defaultTheme = createTheme({
    overrides: themeOptions
  });
  return defaultTheme;
};
// Generate a preview URL for a themed section
// or entire widget if section is unspecified
export const getThemePreviewUrl = function (theme) {
  let settings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    sectionName,
    subSectionName = '',
    themeMode = ''
  } = settings;
  const isSystemBaseTheme = SYSTEM_THEME_ID === theme.id;
  const themePath = isSystemBaseTheme ? 'default' : theme.slug || theme.id;
  const finalThemeMode = themeMode === ThemeMode.Light ? '' : themeMode;
  const fileName = [sectionName || 'default', subSectionName, finalThemeMode].filter(Boolean).join('-');
  return `${PREVIEW_BASE_URL}/${themePath}/${kebabCase(fileName)}.webp`;
}; // Filters out Google Fonts that don't have correct previews

export const googleFontsFilter = font => {
  if (!font) return false;
  if (['ponnala', 'redacted'].includes(font.sane)) return false; // Exclude specific fonts

  if (font.sane.startsWith('material_')) return false; // Exclude Material Design fonts

  if (font.sane.startsWith('yarndings_')) return false; // Exclude Yarndings fonts

  return true;
};
export { createTheme };